/*
 * Monsuri PDP — "Complete the set" single/set toggle.
 *
 * Reuses the theme's existing ajax add-to-cart + cart drawer (app.js `.ajax-submit`):
 * the product form must serialize EXACTLY ONE `id`. Exclusion is by the `name` attribute,
 * NOT `disabled` — the theme's OptionSelectors OWNS this variant <select> and re-enables it
 * behind our back, so a `disabled`-based exclusion lets a second `id=` leak in and /cart/add
 * rejects the double id. On "Set" we strip the <select>'s `name` and inject a hidden
 * `name="id"` carrying the kit variant; on "Single" we strip the hidden's name and restore the
 * select's. So the same Add-to-cart adds whichever is selected. No custom cart fetch, no
 * _bundle_id, no redirect — the drawer just works.
 *
 * The theme's selectCallback (sections.js) dispatches the select `change` and THEN
 * re-stamps the ATC label + availability. So we (a) defer our Set re-assertion to
 * after that callback, (b) force the buy button back to available in Set mode (the
 * kit, guarded available in Liquid, is what we submit — not the single variant), and
 * (c) hide the dynamic-checkout (Shop Pay) button in Set mode, since it can't be
 * reliably re-pointed at the kit and would otherwise express-checkout the single.
 *
 * Vanilla, dependency-free, idempotent.
 */
(function () {
  'use strict';

  function findForm(root) {
    var scope = root.closest('.product__details') || root.closest('.product-main');
    return scope ? scope.querySelector('[data-product-form] form') : null;
  }

  function money(cents, format) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try { return window.Shopify.formatMoney(cents, format); } catch (e) { /* fall through */ }
    }
    return '$' + (cents / 100).toFixed(2); // display-only fallback; cart total uses Shopify's real formatting
  }

  function initSet(root) {
    if (root.hasAttribute('data-pdp-set-ready')) return; // idempotent
    root.setAttribute('data-pdp-set-ready', '');

    var form = findForm(root);
    if (!form) return; // graceful: radios remain a static display

    var wrap = form.closest('[data-product-form]');
    // The canonical variant field. This theme's product-form.liquid always renders a
    // <select name="id"> (even on single-variant products), but accept the common hidden
    // <input name="id"> pattern too so a future form refactor can't silently produce a
    // DOUBLE id serialization in set mode (our kit hidden + an unmatched theme input that
    // never got its name stripped -- /cart/add.js rejects double ids and nothing adds).
    // :not([data-pdp-set-id]) excludes our own kit hidden if it survives a re-init.
    // (Codex adversarial review 2026-07-07, finding 3.)
    var select = form.querySelector('select[name="id"], input[name="id"]:not([data-pdp-set-id])');
    var atcBtn = form.querySelector('.add_to_cart');
    var atcText = form.querySelector('.add_to_cart .text');
    var btnsWrap = form.querySelector('.purchase-details__buttons');
    var payBtn = form.querySelector('.shopify-payment-button');
    var singlePriceEl = root.querySelector('[data-pdp-set-single-price]');

    // The hero price above the buy-box (the theme's own price-ui, [data-price-ui]) previously
    // never updated on Set/Single toggle -- it only reacts to the variant <select> changing, and
    // Set mode deliberately never touches that select's VALUE (only its name attr, for form
    // serialization). Result: selecting "Set" relabels the Add button to "Add the set" and shows
    // $74.95 in the set's own row, but the big price up top stayed frozen on the single item's
    // price -- a real, operator-flagged inconsistency (two different prices visible for one
    // pending action). Mirrors the same swap `pdp-sticky-atc.js` already does correctly for the
    // sticky bar, using the SAME data-single-price/data-set-price attributes (already on `root`,
    // just never read here before).
    //
    // price-ui's .price element is NOT always a single .money span: on a price_varies product
    // (8 complete-the-set hosts have real multi-price variants, e.g. the bath salts) the
    // server render and the variant==null (invalid option combo) re-render are a RANGE with
    // TWO .money spans (price-min + price-max). Writing only the FIRST .money would produce a
    // mangled "$215.65 - $22.00" in set mode (codex adversarial review 2026-07-07, finding 2).
    // So the set/single paint REPLACES the .price element's whole content with one .money span,
    // and the single-mode ambiguous case (no resolvable variant) restores the captured server
    // markup instead. Elements are looked up FRESH each sync -- price-ui re-renders its subtree.
    var scopeEl = root.closest('.product__details') || root.closest('.product-main');
    function heroPriceEl() { return scopeEl ? scopeEl.querySelector('[data-price-ui] .price') : null; }
    function heroCompareEl() { return scopeEl ? scopeEl.querySelector('[data-price-ui] .compare-at-price') : null; }
    var heroPriceFallbackHTML = (function () { var el = heroPriceEl(); return el ? el.innerHTML : ''; })(); // server render (single OR range), restored when no live price resolves
    var heroCompareFallbackHTML = (function () { var el = heroCompareEl(); return el ? el.innerHTML : ''; })();
    var heroPriceHadSale = (function () { var el = heroPriceEl(); return !!(el && el.classList.contains('price--sale')); })();
    function paintHeroPrice(el, text) {
      var span = document.createElement('span');
      span.className = 'money';
      span.setAttribute('data-price', '');
      span.textContent = text; // shop-formatted money strings only (Liquid | money / Shopify.formatMoney) -- never user input
      el.innerHTML = '';
      el.appendChild(span);
    }

    var setVariant = root.getAttribute('data-set-variant');
    var singleLabel = root.getAttribute('data-single-label') || (atcText ? atcText.textContent.trim() : '');
    var setLabel = root.getAttribute('data-set-label') || 'Add the set';
    if (!setVariant) return;
    root.hidden = false; // form + kit variant confirmed → reveal the now-functional control

    var product = null;
    if (wrap && wrap.getAttribute('data-product')) {
      try { product = JSON.parse(wrap.getAttribute('data-product')); } catch (e) { /* no live price */ }
    }
    var moneyFormat = (wrap && wrap.getAttribute('data-money-format')) || '${{amount}}';

    var hidden = null;
    function ensureHidden() {
      if (!hidden) {
        hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'id';
        hidden.value = setVariant;
        hidden.setAttribute('data-pdp-set-id', '');
        form.appendChild(hidden);
      }
      return hidden;
    }

    var isSet = false;
    var soldOutLabel = (window.PXUTheme && window.PXUTheme.translation && window.PXUTheme.translation.sold_out) || 'Sold out';

    // Real single-variant availability from the parsed product JSON — NEVER scraped from the
    // ATC's live DOM, which the theme ajax handler mutates (disabled + zoomOut) mid-add.
    function singleAvailable() {
      if (!product || !product.variants || !select) return true;
      var vid = parseInt(select.value, 10);
      for (var i = 0; i < product.variants.length; i++) {
        if (product.variants[i].id === vid) return !!product.variants[i].available;
      }
      return true;
    }

    // Give apply() SOLE ownership of the label: set the text AND clear the theme add-animation
    // classes. The theme hides the label via `animated zoomOut` (opacity 0) for the whole
    // in-flight add; without this, a mid-add toggle leaves OUR re-labelled text stuck invisible
    // (the "button text disappeared" bug).
    function setLabelText(txt) {
      if (!atcText) return;
      atcText.textContent = txt;
      atcText.classList.remove('animated', 'zoomOut', 'zoomIn', 'fadeInDown', 'fadeOutUp');
      var chk = atcBtn && atcBtn.querySelector('.checkmark');
      if (chk) chk.classList.remove('checkmark-active');
    }

    // The theme ajax add DISABLES the button + hides the label (`zoomOut`) / flashes a `.checkmark`
    // for its whole in-flight window. While it is busy we must NOT re-enable the button — doing so
    // defeats the theme's own re-entrancy guard and lets a rapid toggle+click fire an overlapping
    // (double) add. We leave the button as the theme set it during the add and re-assert the current
    // mode's real enabled state once the theme's success timer has run.
    var renorm = null;
    function themeBusy() {
      return !!(atcText && atcText.classList.contains('zoomOut')) ||
             !!(atcBtn && atcBtn.querySelector('.checkmark.checkmark-active'));
    }
    function desiredLabel() { return isSet ? setLabel : (singleAvailable() ? singleLabel : soldOutLabel); }
    function paintEnabled() {
      var disabled = isSet ? false : !singleAvailable();   // the kit is Liquid-guarded available; single from JSON
      if (atcBtn) { atcBtn.disabled = disabled; atcBtn.classList.toggle('disabled', disabled); }
      if (btnsWrap) btnsWrap.classList.toggle('product-is-unavailable', disabled);
    }

    // Exactly ONE `name="id"` may ever serialize. The theme's OptionSelectors OWNS this <select>
    // (it is the variantIdField) and RE-ENABLES it behind our back on every variant re-stamp, so
    // `select.disabled` is NOT a reliable exclusion — a re-enabled select posts a SECOND `id=`
    // alongside our kit hidden and /cart/add.js rejects the double id (the "nothing added" bug).
    // So gate serialization on the `name` attribute (which the theme never touches): set-mode
    // strips the select's name; single-mode strips the hidden's name.
    function apply(next) {
      isSet = next;
      // Busy if the theme is mid-add OR a prior mid-add toggle's re-assert is still pending — the
      // latter keeps it STICKY across repeated toggles (a 2nd toggle can't see themeBusy() false just
      // because our own setLabelText() cleared zoomOut, which would reopen the double-add window).
      var busy = themeBusy() || !!renorm;
      var h = ensureHidden();
      // FORM — name-based exclusion (always safe; independent of the button lifecycle).
      if (isSet) { h.setAttribute('name', 'id'); h.disabled = false; if (select) { select.removeAttribute('name'); select.disabled = true; } }
      else { h.removeAttribute('name'); h.disabled = true; if (select) { select.setAttribute('name', 'id'); select.disabled = false; } }
      if (payBtn) payBtn.style.display = isSet ? 'none' : '';                    // Shop Pay can't be re-pointed at the kit
      root.setAttribute('data-active', isSet ? 'set' : 'single');
      // LABEL — always visible + correct (never leave OUR text stuck under the theme's zoomOut).
      setLabelText(desiredLabel());
      syncHeroPrice();                                                          // hero price mirrors the active selection
      // ENABLED STATE — don't fight the theme's in-flight disable; re-assert once it settles.
      if (renorm) { clearTimeout(renorm); renorm = null; }
      if (busy) {
        var tries = 0;
        renorm = window.setTimeout(function reassert() {
          if (themeBusy() && ++tries < 12) { renorm = window.setTimeout(reassert, 400); return; } // wait for the add to finish (capped ~5.5s so a hung/foreign marker can't loop forever)
          renorm = null; paintEnabled(); setLabelText(desiredLabel());           // theme done (or capped) → re-assert current mode (esp. sold-out single)
        }, 1100);
      } else {
        paintEnabled();
      }
    }

    function currentSingleVariantPrice() {
      if (!product || !product.variants || !select) return null;
      var vid = parseInt(select.value, 10);
      for (var i = 0; i < product.variants.length; i++) {
        if (product.variants[i].id === vid) return product.variants[i].price;
      }
      return null;
    }

    function syncSinglePrice() {
      if (!singlePriceEl) return;
      var cents = currentSingleVariantPrice();
      if (cents != null) singlePriceEl.textContent = money(cents, moneyFormat);
    }

    // Hero price above the buy-box: Set → the kit's own price (data-set-price); Single → the
    // live single-variant price (same lookup as syncSinglePrice, so a variant change stays correct
    // even while this ran once already), falling back to the server-rendered default if the
    // product JSON didn't parse. Compare-at: kits never carry a real one (Monsuri's no-fake-
    // discount law), so clear it in Set mode rather than leave a stale single-item strikethrough
    // under a different price; restore the server default on Single.
    function syncHeroPrice() {
      // innerHTML here is safe: the only non-empty assignment restores heroPriceFallbackHTML /
      // heroCompareFallbackHTML -- verbatim snapshots of THIS page's own Liquid-rendered markup
      // captured at init (never user input, never remote content); the live price value itself
      // goes through paintHeroPrice's textContent.
      var priceEl = heroPriceEl();
      if (priceEl) {
        if (isSet) {
          var setPrice = root.getAttribute('data-set-price');
          if (setPrice) paintHeroPrice(priceEl, setPrice);
          priceEl.classList.remove('price--sale'); // the kit's own price is never a markdown (no-fake-discount law)
        } else {
          var cents = currentSingleVariantPrice();
          if (cents != null) paintHeroPrice(priceEl, money(cents, moneyFormat));
          else priceEl.innerHTML = heroPriceFallbackHTML; // no resolvable variant -> the server render (single OR range)
          priceEl.classList.toggle('price--sale', heroPriceHadSale);
        }
      }
      var compareEl = heroCompareEl();
      if (compareEl) compareEl.innerHTML = isSet ? '' : heroCompareFallbackHTML;
    }

    var radios = root.querySelectorAll('input[type="radio"]');
    for (var i = 0; i < radios.length; i++) {
      radios[i].addEventListener('change', function (e) {
        apply(e.target.value === 'set' && e.target.checked);
      });
    }

    // The theme's selectCallback dispatches this change then re-stamps the button.
    // Defer the Set re-assertion so it runs AFTER that callback and wins.
    if (select) {
      select.addEventListener('change', function () {
        syncSinglePrice();
        syncHeroPrice();
        // Re-assert set mode AFTER the theme's selectCallback re-stamps the button — but re-check
        // isSet INSIDE the timeout so a set→single toggle in the same tick can't be clobbered by a
        // stale apply(true).
        if (isSet) window.setTimeout(function () { if (isSet) apply(true); }, 0);
      });
    }

    var checked = root.querySelector('input[type="radio"]:checked');
    apply(!!checked && checked.value === 'set'); // single by default
    syncSinglePrice();
    syncHeroPrice();
  }

  // Quick-view modal: a native <dialog> (browser handles ESC + focus-trap). Open on the "Quick view"
  // button, close on the X / backdrop click. Idempotent; independent of the single/set form logic.
  function initQuickView() {
    var btns = document.querySelectorAll('[data-pdp-qv-open]');
    for (var i = 0; i < btns.length; i++) {
      var btn = btns[i];
      if (btn.hasAttribute('data-pdp-qv-ready')) continue;
      btn.setAttribute('data-pdp-qv-ready', '');
      var dlg = document.getElementById(btn.getAttribute('data-pdp-qv-target'));
      if (!dlg) continue;
      (function (btn, dlg) {
        btn.addEventListener('click', function () {
          if (typeof dlg.showModal === 'function') { try { dlg.showModal(); return; } catch (e) { /* already open */ } }
          dlg.setAttribute('open', ''); // very old browsers: non-modal fallback
        });
        var closers = dlg.querySelectorAll('[data-pdp-qv-close]');
        for (var j = 0; j < closers.length; j++) closers[j].addEventListener('click', function () { dlg.close(); });
        dlg.addEventListener('click', function (e) { if (e.target === dlg) dlg.close(); }); // click the backdrop area (outside the panel)
      })(btn, dlg);
    }
  }

  function initAll() {
    var roots = document.querySelectorAll('[data-pdp-set]');
    for (var i = 0; i < roots.length; i++) initSet(roots[i]);
    initQuickView();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll); // theme-editor re-render
})();
